import { fetchAlgoliaResults } from '@theme/solstice-algolia-client';

/**
 * Collection/search product grid + filter panel, driven directly by the
 * Algolia Search API rather than the Algolia Shopify app's InstantSearch
 * widget.
 *
 * Everything Algolia-specific is isolated in `fetchAlgoliaResults` and
 * `buildAlgoliaParams` below -- switching search providers later means
 * rewriting those two functions and `normalizeHit`, not the rendering code
 * or the custom element that drives the page.
 */

/**
 * The 13 filters this theme shows, in display order.
 *
 * `paramName` matches Shopify's own native filter URL convention
 * (`filter.p.m.<namespace>.<key>` is exactly what `collection.filters` uses
 * for a product-metafield list filter -- see shopify.dev's filtering docs),
 * so a URL this section produces looks and behaves like one Shopify's native
 * filter form would, and stays compatible if that native form is ever
 * reintroduced. `price_range` is the one exception: Shopify's native price
 * filter is a numeric range (`filter.v.price.gte`/`.lte`), not a list, but
 * Algolia's `price_range` attribute here is a bucketed string ("900:1000").
 * `gteParam`/`lteParam` bridge that gap for the URL only -- see
 * `buildUrlParams`/`readStateFromUrl`.
 *
 * `type` picks which control `renderFacetGroup` renders: `'checkbox'` (plain
 * list), `'swatch'` (color chips), or `'price'` (the numeric range slider --
 * special-cased throughout: it doesn't use `attribute`/facetFilters at all,
 * see buildAlgoliaParams and PRICE_FACET below).
 *
 * `searchable: true` adds a local, case-insensitive substring search box.
 * Search and "View more" use the main response's up to 500 facet values;
 * neither interaction sends another request.
 */
const FACET_CONFIG = [
  { key: 'brand', attribute: 'meta.custom.brand', label: 'Brands', type: 'checkbox', searchable: true, collapsedByDefault: false, paramName: 'filter.p.m.custom.brand' },
  { key: 'ships_in', attribute: 'meta.custom.ships_in', label: 'Ships in', type: 'checkbox', collapsedByDefault: false, paramName: 'filter.p.m.custom.ships_in' },
  { key: 'product_types', attribute: 'meta.custom.product_types', label: 'Eyewear Type', type: 'checkbox', collapsedByDefault: false, paramName: 'filter.p.m.custom.product_types' },
  { key: 'features', attribute: 'meta.custom.features', label: 'Features', type: 'checkbox', collapsedByDefault: false, paramName: 'filter.p.m.custom.features' },
  { key: 'age_groups', attribute: 'meta.custom.age_groups', label: 'Age group', type: 'checkbox', collapsedByDefault: false, paramName: 'filter.p.m.custom.age_groups' },
  { key: 'genders', attribute: 'meta.custom.genders', label: 'Gender', type: 'checkbox', collapsedByDefault: false, paramName: 'filter.p.m.custom.genders' },
  { key: 'color_groups', attribute: 'meta.custom.color_groups', label: 'Color', type: 'swatch', collapsedByDefault: false, paramName: 'filter.p.m.custom.color_groups' },
  { key: 'shapes', attribute: 'meta.custom.shapes', label: 'Frame shape', type: 'checkbox', collapsedByDefault: false, paramName: 'filter.p.m.custom.shapes' },
  { key: 'frame_materials', attribute: 'meta.custom.frame_materials', label: 'Material', type: 'checkbox', collapsedByDefault: false, paramName: 'filter.p.m.custom.frame_materials' },
  { key: 'price_range', attribute: 'price', label: 'Price Range', type: 'price', collapsedByDefault: false, paramName: 'filter.p.m.custom.price_range', gteParam: 'filter.v.price.gte', lteParam: 'filter.v.price.lte' },
  { key: 'lens_widths', attribute: 'meta.custom.lens_widths', label: 'Lens width', type: 'checkbox', collapsedByDefault: false, paramName: 'filter.p.m.custom.lens_widths' },
  { key: 'temple_lengths', attribute: 'meta.custom.temple_lengths', label: 'Temple length', type: 'checkbox', collapsedByDefault: false, paramName: 'filter.p.m.custom.temple_lengths' },
  { key: 'bridges', attribute: 'meta.custom.bridges', label: 'Bridge', type: 'checkbox', collapsedByDefault: false, paramName: 'filter.p.m.custom.bridges' },
];

/** A small, curated color-name -> CSS color mapping for the Color swatch
 * facet. No hex/swatch data exists anywhere for these values -- they're
 * plain strings from the `custom.color_groups` metafield, not tied to
 * Shopify's own metafield "color" swatch config the way a native swatch
 * filter's `filter.values[].swatch.color` would be. Unmapped names still
 * render (as a neutral placeholder swatch + the text label), so a color
 * missing from this list is a visual gap, not a functional one. */
/**
 * Checked against every real value currently in the catalog (queried
 * Algolia's own `meta.custom.color_groups` facet directly -- 17 distinct
 * values, none guessed) rather than assuming a generic color-name list would
 * cover it. No admin-configured swatch/color data exists for this metafield
 * to reuse (confirmed: `snippets/swatch.liquid`'s color system is for
 * product variant OPTIONS, a different metafield entirely, and
 * `custom.color_groups` is a plain text-list field with no metaobject/swatch
 * definition behind it) -- this hardcoded map is genuinely the only source.
 * Revisit if a new color value shows up in the catalog that isn't listed
 * here (falls back to a neutral placeholder swatch + the text label, so a
 * gap is a visual miss, not a functional one).
 * @type {Record<string, string>}
 */
const COLOR_SWATCH_MAP = {
  black: '#101010',
  white: '#f5f5f5',
  grey: '#8a8a8a',
  brown: '#6b4423',
  tortoise: '#5c3a21',
  gold: '#c9a227',
  silver: '#c0c0c0',
  blue: '#2b4c7e',
  green: '#3f6b4a',
  red: '#a3352f',
  pink: '#e0a3b0',
  violet: '#6b4c8a',
  yellow: '#e0c341',
  orange: '#c8722f',
  transparent: '#f5f5f5',
  turquoise: '#3f9a9a',
  multicolor: 'conic-gradient(from 0deg, #e0574f, #e0c341, #3f6b4a, #2b4c7e, #6b4c8a, #e0574f)',
};

/** Outlet collection hides two facets entirely. */
const OUTLET_HIDDEN_FACET_KEYS = new Set(['ships_in', 'features']);

/**
 * Both cadences below (mobile: every 4 products; desktop: 2 full rows then
 * every 12 products, alternating side) mirror sections/main-collection.liquid
 * exactly -- see that file for the Figma-derived reasoning. Kept as two
 * independent counters for the same reason: they are genuinely different
 * rhythms, not one cadence toggled by breakpoint.
 */
const MOBILE_BANNER_INTERVAL = 4;
const DESKTOP_BANNER_START = 8;
const DESKTOP_BANNER_INTERVAL = 12;

/** Configured sort replicas. Render this list locally; probing replicas on
 * every collection visit wastes one search request per option.
 * indexName: null uses the configured base index. Price sorts stay disabled
 * until indexed and displayed discounted prices agree. */
const SORT_CANDIDATES = [
  { value: 'relevance', name: 'Relevance', indexName: null },
  { value: 'best-selling', name: 'Best selling', indexName: 'shopify_products_recently_ordered_count_desc' },
  { value: 'title-ascending', name: 'Alphabetically, A-Z', indexName: 'shopify_products_title_asc' },
  { value: 'title-descending', name: 'Alphabetically, Z-A', indexName: 'shopify_products_title_desc' },
  { value: 'created-descending', name: 'Date, new to old', indexName: 'shopify_products_published_at_desc' },
  { value: 'created-ascending', name: 'Date, old to new', indexName: 'shopify_products_published_at_asc' },
];

/**
 * Reads sort/facet/page state from the current URL, matching Shopify's own
 * native param names (see FACET_CONFIG's comment and SORT_CANDIDATES'
 * `value`s, which already equal Shopify's `sort_by` enum). Called once on
 * load so a shared/bookmarked/reloaded URL restores exactly what it showed.
 */
/**
 * Every facet's selected value(s) except price_range is a plain string
 * array; price_range alone is `{min?, max?}` (see FACET_CONFIG's `type`
 * comment). A bare `{}` object literal infers as the empty-object type with
 * no index signature, so any dynamic `[facet.key]` access on one needs this
 * annotated at the construction site.
 * price_range's own {min,max} shape is cast individually at its few access
 * points instead of folded into this type -- a per-key union here made every
 * *other* facet's array access ambiguous too (TS can't discriminate a
 * Record's value type by which string key was used).
 * @typedef {Record<string, string[]>} SelectedFacets
 */

/** @typedef {{selectedFacets: SelectedFacets, sortValue: string | null, page: number, searchTerm: string}} UrlState */

function readStateFromUrl() {
  const params = new URLSearchParams(window.location.search);
  /** @type {SelectedFacets} */
  const selectedFacets = {};

  FACET_CONFIG.forEach((facet) => {
    if (facet.gteParam || facet.lteParam) return; // price_range -- handled separately below
    const values = params.getAll(facet.paramName);
    if (values.length) selectedFacets[facet.key] = values;
  });

  // price_range is a real numeric range (Algolia's genuine `price` field, see
  // FACET_CONFIG), so its URL form (filter.v.price.gte/.lte, Shopify's own
  // native price-filter params) round-trips exactly as {min, max} -- no
  // bucket-string reconstruction needed.
  const priceFacet = FACET_CONFIG.find((f) => f.key === PRICE_FACET_KEY);
  // gteParam/lteParam only exist on FACET_CONFIG's price_range entry, so TS
  // infers them as `string | undefined` on the array's general element type
  // even once narrowed down to this specific entry -- cast, since we know
  // structurally this IS that entry.
  const gte = priceFacet ? params.get(/** @type {string} */ (priceFacet.gteParam)) : null;
  const lte = priceFacet ? params.get(/** @type {string} */ (priceFacet.lteParam)) : null;
  if (priceFacet && (gte || lte)) {
    // price_range is the one key whose value isn't a string[] -- see
    // SelectedFacets' own comment on why that's cast here instead of typed
    // in generally.
    selectedFacets[priceFacet.key] = /** @type {any} */ ({
      min: gte ? Number(gte) : undefined,
      max: lte ? Number(lte) : undefined,
    });
  }

  const sortParam = params.get('sort_by');
  const pageParam = Number(params.get('page'));

  return {
    selectedFacets,
    sortValue: sortParam || null,
    // Shopify's own `page` param is 1-indexed; Algolia's `page` is 0-indexed.
    page: Number.isFinite(pageParam) && pageParam > 1 ? pageParam - 1 : 0,
    // Shopify's native search box already submits to `/search?q=...` (a full
    // page load), so `q` is simply whatever's already in the URL on
    // connectedCallback -- no separate data-attribute plumbing needed. Kept
    // in UrlState/#state purely so writeStateToUrl's replaceState calls (on
    // facet/sort/page changes) don't drop it -- see buildUrlParams.
    searchTerm: params.get('q') || '',
  };
}

const PRICE_FACET_KEY = 'price_range';

/**
 * Matches FACET_CONFIG's 'color_groups' entry. Whether THIS specific facet
 * (not any of the other 12) is active decides whether a card's fetch is
 * pinned to Algolia's matched variant -- see #renderGrid's `hasColorFilter`.
 * Confirmed with the user: with no colour filter active, a card shows the
 * product's own default featured image/variant, same as before this
 * feature existed; a colour filter is the one case where showing whatever
 * variant the customer is actually browsing for (not the default) is the
 * point. Every other facet (brand, gender, price, etc.) leaves the card
 * exactly as it was.
 */
const COLOR_FACET_KEY = 'color_groups';

/**
 * price_range's value shape is `{min?, max?}` (real numeric bounds, see
 * PRICE_FACET_KEY's comment in buildAlgoliaParams), not an array like every
 * other facet -- readStateFromUrl/buildUrlParams special-case it via
 * gteParam/lteParam (Shopify's own native price-filter param names) while
 * every other facet round-trips through the generic paramName loop above.
 */

/** Inverse of readStateFromUrl -- builds the query string a given state should show.
 * @param {UrlState} state */
function buildUrlParams(state) {
  const params = new URLSearchParams();

  FACET_CONFIG.forEach((facet) => {
    if (facet.gteParam || facet.lteParam) {
      // Both casts below: gteParam/lteParam only exist on the price_range
      // entry (see readStateFromUrl's matching comment), and its selected
      // value is {min?,max?} rather than the general string[] shape (see
      // SelectedFacets' own comment).
      const range = /** @type {{min?: number, max?: number} | undefined} */ (state.selectedFacets[facet.key]);
      if (range?.min != null) params.set(/** @type {string} */ (facet.gteParam), String(range.min));
      if (range?.max != null) params.set(/** @type {string} */ (facet.lteParam), String(range.max));
      return;
    }

    const values = state.selectedFacets[facet.key] || [];
    if (!values.length) return;
    values.forEach((value) => params.append(facet.paramName, value));
  });

  // Always set once there's a state to reflect (writeStateToUrl/#syncUrl is
  // only ever called from an explicit change handler, never on passive page
  // load) -- switching sort AWAY FROM the default and back TO it are both
  // real user actions and should both show up in the URL, not just the
  // former. Suppressing the default's own value here would make picking
  // "Relevance" again look like sort was never touched.
  if (state.sortValue) {
    params.set('sort_by', state.sortValue);
  }

  if (state.page > 0) {
    params.set('page', String(state.page + 1)); // back to Shopify's 1-indexed convention
  }

  // Round-trips the search page's `q` through every replaceState call this
  // fires (facet/sort/page changes) -- otherwise the first filter click on
  // /search?q=sunglasses would silently drop back to an unscoped query.
  if (state.searchTerm) {
    params.set('q', state.searchTerm);
  }

  return params;
}

/** Reflects current state into the URL without adding a history entry per
 * toggle/click (a real Shopify filter-form submit does add one per
 * navigation, but that's a full page load each time -- this is AJAX-only, so
 * pushState-per-checkbox would flood back/forward with noise).
 * @param {UrlState} state */
function writeStateToUrl(state) {
  const query = buildUrlParams(state).toString();
  const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
  window.history.replaceState(window.history.state, '', url);
}

/**
 * Caches Algolia responses in localStorage with a TTL, so a repeat visit
 * within that window -- even a fresh page load, even a different tab
 * (localStorage is shared per origin) -- reuses the last response instead of
 * hitting Algolia again. A plain in-memory cache can't do this: a full page
 * reload creates a fresh JS module scope, wiping any ordinary variable.
 *
 * Scoped by the exact request signature (index + params JSON), not by
 * "is this the default query" -- so the default view, a specific filter
 * combination, and a specific sort all get cached the same uniform way: a
 * repeated exact combination hits the cache, a novel one is a live miss.
 * Product card content itself (assets/solstice__algolia-search.js's
 * `fetchCardHtml`) is NOT cached this way -- only per-page-load in memory --
 * so prices/stock on a card never go stale even when the hit list does.
 */
const QUERY_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const QUERY_CACHE_PREFIX = 'solstice_algolia_cache:';

/**
 * @param {string} indexName
 * @param {object} params
 */
function queryCacheKey(indexName, params) {
  return `${QUERY_CACHE_PREFIX}${indexName}:${JSON.stringify(params)}`;
}

/**
 * @param {string} indexName
 * @param {object} params
 */
function readQueryCache(indexName, params) {
  try {
    const raw = window.localStorage.getItem(queryCacheKey(indexName, params));
    if (!raw) return null;
    const { timestamp, data } = JSON.parse(raw);
    if (typeof timestamp !== 'number' || Date.now() - timestamp > QUERY_CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null; // private browsing / storage disabled / corrupt entry -- just skip the cache
  }
}

/**
 * @param {string} indexName
 * @param {object} params
 * @param {any} data
 */
function writeQueryCache(indexName, params, data) {
  try {
    window.localStorage.setItem(queryCacheKey(indexName, params), JSON.stringify({ timestamp: Date.now(), data }));
  } catch {
    // Quota exceeded or storage disabled -- caching is a pure optimization, safe to drop.
  }
}

/** Sweeps expired entries so the cache doesn't grow forever across visits --
 * called once per page load, not on every query. */
function pruneExpiredQueryCache() {
  try {
    const now = Date.now();
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(QUERY_CACHE_PREFIX)) continue;
      try {
        const { timestamp } = JSON.parse(/** @type {string} */ (window.localStorage.getItem(key)));
        if (typeof timestamp !== 'number' || now - timestamp > QUERY_CACHE_TTL_MS) window.localStorage.removeItem(key);
      } catch {
        window.localStorage.removeItem(key); // corrupt entry -- drop it
      }
    }
  } catch {
    // Storage disabled -- nothing to prune.
  }
}

/** Cache-checked wrapper around fetchAlgoliaResults -- see the QUERY_CACHE_*
 * comment above for what's cached and why. Still accepts/forwards `signal`
 * on a cache miss so a superseded request (rapid filter/sort clicks) is
 * still abortable exactly as before caching existed -- only the network
 * fetch itself is skippable, not the abort contract callers rely on.
 * @param {{appId: string, apiKey: string, indexName: string, params: object, signal?: AbortSignal}} options
 */
async function fetchAlgoliaResultsCached({ appId, apiKey, indexName, params, signal }) {
  const cached = readQueryCache(indexName, params);
  if (cached) return cached;
  const data = await fetchAlgoliaResults({ appId, apiKey, indexName, params, signal });
  writeQueryCache(indexName, params, data);
  return data;
}

/** @param {{collectionId?: string, page: number, hitsPerPage: number, selectedFacets: SelectedFacets, searchTerm?: string}} state */
function buildAlgoliaParams(state) {
  // price_range's value is `{min?, max?}`, not an array -- `.length` is
  // undefined on it, so this naturally excludes it from the generic
  // facetFilters loop without a special case here. It's filtered
  // numerically below instead (Algolia's real `price` field, not
  // facetFilters -- see FACET_CONFIG's comment on the `price` type).
  const facetFilters = FACET_CONFIG.filter((f) => state.selectedFacets[f.key]?.length).map((f) =>
    [...(state.selectedFacets[f.key] || [])].sort().map((value) => `${f.attribute}:${value}`)
  );

  if (state.collectionId) {
    facetFilters.push([`collection_ids:${state.collectionId}`]);
  }

  // Cast: price_range is the one key whose value isn't a string[] -- see
  // SelectedFacets' own comment.
  const priceRange = /** @type {{min?: number, max?: number} | undefined} */ (state.selectedFacets[PRICE_FACET_KEY]);
  const numericFilters = [];
  if (priceRange?.min != null) numericFilters.push(`price>=${priceRange.min}`);
  if (priceRange?.max != null) numericFilters.push(`price<=${priceRange.max}`);

  return {
    // Empty on collection pages (state.searchTerm is never set there) -- an
    // empty query returns all records sorted by customRanking, same as
    // before this existed. Only the search page ever passes a non-empty
    // value here (see #fetchResults' buildAlgoliaParams call).
    query: state.searchTerm || '',
    page: state.page,
    hitsPerPage: state.hitsPerPage,
    // Requesting 'price' here is also what makes Algolia return
    // `facets_stats.price.min/max` in the response -- used to size the
    // slider's bounds to what's actually in this collection (see
    // #renderFacets/renderFacetGroup's 'price' branch).
    facets: FACET_CONFIG.map((f) => f.attribute),
    facetFilters,
    numericFilters,
    // The index has Distinct enabled (attributeForDistinct: "handle") so
    // nbHits/hits are already one record per product -- but facet VALUE
    // counts are computed on the raw pre-dedup records unless this is also
    // set, so a product with 2 matching variants would count as 2 in every
    // facet's sidebar count while the grid (correctly) shows 1 card.
    facetingAfterDistinct: true,
    // Load values once for local brand search and facet expansion.
    maxValuesPerFacet: 500,
  };
}

/** @param {unknown} value */
function escapeHtml(value) {
  /** @type {Record<string, string>} */
  const escapes = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return String(value ?? '').replace(/[&<>"']/g, (c) => escapes[c] ?? c);
}

/**
 * Fetches a product card's real markup through the theme's own Liquid
 * rendering (see templates/product.solstice-algolia-card.liquid) instead of
 * rebuilding it from Algolia's hit fields in JS. This means correct
 * session-localized pricing, real swatches, and the exact same "first
 * available, non-hidden variant" link logic as the native collection grid --
 * with no API token and no currency-conversion math of our own to maintain.
 *
 * Cached by handle+variant at module scope: the same product can reappear
 * across different searches/filters within a page visit, but which variant
 * Algolia matched (see the `variantId`/objectID comment below) can differ
 * between those appearances, so the cache key has to include it too -- a
 * handle-only key would silently serve one filter's matched-variant markup
 * to a different filter that matched a different variant of the same
 * product.
 * @type {Map<string, Promise<string>>}
 */
const cardHtmlCache = new Map();

/**
 * @param {string} handle
 * @param {string | number} [variantId] - Algolia's `objectID` for this hit.
 *   The index is one record per variant (confirmed directly against the
 *   live index), so this is the specific variant that matched whatever
 *   facet filters/search produced this hit -- passed through as `?variant=`,
 *   which Shopify resolves server-side into `product.selected_variant`
 *   (see snippets/solstice__product-card.liquid's own comment on this).
 */
function fetchCardHtml(handle, variantId) {
  const cacheKey = variantId != null ? `${handle}:${variantId}` : handle;
  if (!cardHtmlCache.has(cacheKey)) {
    const variantParam = variantId != null ? `&variant=${encodeURIComponent(variantId)}` : '';
    cardHtmlCache.set(
      cacheKey,
      fetch(`/products/${handle}?view=solstice-algolia-card${variantParam}`)
        .then((response) => (response.ok ? response.text() : ''))
        .catch(() => '')
    );
  }
  return /** @type {Promise<string>} */ (cardHtmlCache.get(cacheKey));
}

// Liquid intentionally emits no card when every variant fails the shared
// eligibility rule. Its response can still contain whitespace, so a truthy
// response string alone does not mean there is a grid item to display.
function hasRenderableCard(html) {
  return /<solstice-product-card(?:\s|>)/i.test(html);
}

/**
 * Fetches the collection's promotional grid-banner tiles (see
 * templates/collection.solstice-algolia-banners.liquid) once per collection
 * handle. Shopify Liquid has no access to arbitrary custom query-string
 * params -- only the `view` param that selects an alternate template -- so
 * unlike a product card, a single banner can't be fetched by index through
 * the theme's own rendering. This is the one piece of the grid rendered from
 * hand-written JS rather than real Liquid markup.
 * @type {Map<string, Promise<any[]>>}
 */
const bannersCache = new Map();

/**
 * @param {string} [handle]
 * @returns {Promise<any[]>}
 */
function fetchBanners(handle) {
  if (!handle) return Promise.resolve([]);
  if (!bannersCache.has(handle)) {
    bannersCache.set(
      handle,
      fetch(`/collections/${handle}?view=solstice-algolia-banners`)
        .then((response) => (response.ok ? response.json() : { banners: [] }))
        .then((data) => data.banners || [])
        .catch(() => [])
    );
  }
  return /** @type {Promise<any[]>} */ (bannersCache.get(handle));
}

/**
 * Mirrors snippets/collection-grid-banner-tile.liquid: a video (if present)
 * plays on both breakpoints with the per-breakpoint image as its poster; a
 * video tile is never a link (native controls need the click); an
 * image-only tile keeps the optional link; a tile with no media at all is
 * skipped entirely.
 * @param {any} banner
 * @param {'mobile'|'desktop'} variant
 * @param {'left'|'right'} [bannerSide]
 */
function buildBannerTileHtml(banner, variant, bannerSide) {
  const tileImage = variant === 'mobile' ? banner.imageMobile : banner.imageDesktop;
  const posterUrl = variant === 'mobile' ? banner.posterMobile : banner.posterDesktop;
  const hasVideo = Boolean(banner.video?.sources?.length);

  if (!hasVideo && !tileImage) return '';

  const isLink = Boolean(banner.link) && !hasVideo;
  const classes = ['product-grid__banner-tile', `product-grid__banner-tile--${variant}`];
  if (variant === 'desktop' && bannerSide) classes.push(`product-grid__banner-tile--${bannerSide}`);

  let media;
  if (hasVideo) {
    const sources = banner.video.sources
      .map((/** @type {any} */ source) => `<source src="${escapeHtml(source.url)}" type="${escapeHtml(source.mimeType)}">`)
      .join('');
    media = `<video class="product-grid__banner-tile-media" ${posterUrl ? `poster="${escapeHtml(posterUrl)}"` : ''} autoplay loop muted playsinline>${sources}</video>`;
  } else {
    media = `<img class="product-grid__banner-tile-media" src="${escapeHtml(tileImage)}" alt="${escapeHtml(banner.heading || '')}" loading="lazy">`;
  }

  const heading = banner.heading ? `<span class="product-grid__banner-tile-heading">${escapeHtml(banner.heading)}</span>` : '';
  const body = `${media}${heading}`;

  return `<li class="${classes.join(' ')}">${
    isLink ? `<a href="${escapeHtml(banner.link)}" class="product-grid__banner-tile-link">${body}</a>` : body
  }</li>`;
}

/**
 * Computes where banner tiles land among a run of product cards, mirroring
 * sections/main-collection.liquid's `global_index`-based checkpoints exactly
 * (same divided_by/modulo arithmetic, same left/right alternation). `startIndex`
 * is the count of unique product cards already rendered before this run, so
 * the cadence stays continuous across "View more" pages.
 * @param {number} startIndex
 * @param {number} count
 * @param {number} bannersCount
 * @returns {{afterIndex: number, variant: 'mobile'|'desktop', side?: 'left'|'right', bannerIndex: number}[]}
 */
function computeBannerInsertions(startIndex, count, bannersCount) {
  /** @type {{afterIndex: number, variant: 'mobile'|'desktop', side?: 'left'|'right', bannerIndex: number}[]} */
  const insertions = [];
  if (!bannersCount) return insertions;

  for (let i = 1; i <= count; i++) {
    const globalIndex = startIndex + i;
    const afterIndex = i - 1;
    // Whether at least one more real product card follows this checkpoint
    // in what's actually being rendered right now -- without this, a
    // checkpoint landing on (or past) the very last renderable card inserts
    // a banner with nothing beside it to share its row, which reads as a
    // squished/empty-looking tile rather than a real 2x2 (desktop) or 1x1
    // (mobile) block. Confirmed via testing: exactly 8 real results (the
    // desktop checkpoint) with nothing after it should show NO banner, not
    // a lonely one.
    const hasTrailingNeighbor = i < count;

    if (globalIndex % MOBILE_BANNER_INTERVAL === 0 && hasTrailingNeighbor) {
      const mobileBannerNumber = Math.floor(globalIndex / MOBILE_BANNER_INTERVAL);
      if (mobileBannerNumber >= 1 && mobileBannerNumber <= bannersCount) {
        insertions.push({ afterIndex, variant: 'mobile', bannerIndex: mobileBannerNumber - 1 });
      }
    }

    if (
      globalIndex >= DESKTOP_BANNER_START &&
      (globalIndex - DESKTOP_BANNER_START) % DESKTOP_BANNER_INTERVAL === 0 &&
      hasTrailingNeighbor
    ) {
      const desktopBannerNumber = Math.floor((globalIndex - DESKTOP_BANNER_START) / DESKTOP_BANNER_INTERVAL) + 1;
      if (desktopBannerNumber <= bannersCount) {
        const side = desktopBannerNumber % 2 === 1 ? 'right' : 'left';
        insertions.push({ afterIndex, variant: 'desktop', side, bannerIndex: desktopBannerNumber - 1 });
      }
    }
  }

  return insertions;
}

/** Algolia's index isn't deduplicated to one hit per product (see the
 * Search options tab's "Display one result per product" setting, currently
 * off), so multiple variants of the same product can appear as separate
 * hits -- keep only the first (highest-ranked) occurrence of each handle,
 * matching how the native grid shows one card per product.
 * @param {{handle: string, objectID?: string}[]} hits */
function dedupeHitsByHandle(hits) {
  const seen = new Set();
  return hits.filter((hit) => {
    if (seen.has(hit.handle)) return false;
    seen.add(hit.handle);
    return true;
  });
}

/** How many values show before a client-side "View more" reveal -- pure
 * CSS/attribute toggle on already-fetched values (see FACET_CONFIG's
 * `searchable` comment), no second request. */
const FACET_VISIBLE_LIMIT = 5;

/** @typedef {typeof FACET_CONFIG[number]} FacetConfig */

const MEASUREMENT_FACET_KEYS = new Set(['bridges', 'temple_lengths', 'lens_widths']);

/** @param {{key: string}} facetConfig @param {string} value */
function measurementSortValue(facetConfig, value) {
  if (!MEASUREMENT_FACET_KEYS.has(facetConfig.key)) return Infinity;
  const match = value.trim().match(/^(\d+(?:[.,]\d+)?|\.\d+)\s*(?:mm)?$/i);
  return match ? Number(match[1].replace(',', '.')) : Infinity;
}

/** Equivalent measurement spellings share one UI identity, not one index value.
 * @param {{key: string}} facetConfig @param {string} value
 */
function facetValueIdentity(facetConfig, value) {
  const number = measurementSortValue(facetConfig, value);
  return Number.isFinite(number) ? String(number) : value;
}

/** @param {{key: string}} facetConfig @param {string} value */
function formatFacetValue(facetConfig, value) {
  if (!MEASUREMENT_FACET_KEYS.has(facetConfig.key)) return value;
  const number = measurementSortValue(facetConfig, value);
  if (Number.isFinite(number)) return `${number} mm`;
  return /mm\b/i.test(value) ? value : `${value.trim()} mm`;
}

/** Group before sorting and applying View more. Alias counts may overlap, so
 * merged rows omit a count rather than inventing a unique-product total.
 * @param {{key: string}} facetConfig @param {Record<string, number>} [facetValues]
 */
function facetOptionEntries(facetConfig, facetValues) {
  const groups = new Map();
  Object.entries(facetValues || {}).forEach(([value, count]) => {
    const identity = facetValueIdentity(facetConfig, value);
    const group = groups.get(identity);
    if (group) group.count = null;
    else groups.set(identity, { value, count });
  });
  const entries = [...groups.values()];
  if (MEASUREMENT_FACET_KEYS.has(facetConfig.key)) {
    entries.sort((a, b) => {
      const left = measurementSortValue(facetConfig, a.value);
      const right = measurementSortValue(facetConfig, b.value);
      return left === right ? 0 : left < right ? -1 : 1;
    });
  }
  return entries;
}

/** Preserve every known raw alias for OR filtering and shareable URLs.
 * @param {{key: string}} facetConfig @param {string[]} current
 * @param {string} value @param {boolean} checked
 * @param {Record<string, number>} [facetValues]
 */
function updateFacetSelection(facetConfig, current, value, checked, facetValues) {
  const identity = facetValueIdentity(facetConfig, value);
  const matches = (candidate) => facetValueIdentity(facetConfig, candidate) === identity;
  if (!checked) return current.filter((candidate) => !matches(candidate));
  return [...new Set([...current, value, ...Object.keys(facetValues || {}).filter(matches)])];
}

/**
 * @param {FacetConfig} facetConfig
 * @param {string} value
 * @param {number | null} count
 * @param {boolean} isSelected
 * @param {number} index
 */
function renderFacetOptionRow(facetConfig, value, count, isSelected, index) {
  const isSwatch = facetConfig.type === 'swatch';
  const swatchStyle = isSwatch
    ? `--solstice-facet-swatch-color: ${COLOR_SWATCH_MAP[value.toLowerCase()] || '#e5e5e5'};`
    : '';

  return `
    <label
      class="solstice__algolia-search-facet-option${isSwatch ? ' solstice__algolia-search-facet-option--swatch' : ''}"
      ${index >= FACET_VISIBLE_LIMIT ? 'data-facet-extra hidden' : ''}
    >
      <input type="checkbox" data-facet-key="${facetConfig.key}" value="${escapeHtml(value)}" ${isSelected ? 'checked' : ''}>
      ${isSwatch ? `<span class="solstice__algolia-search-facet-swatch" style="${swatchStyle}" aria-hidden="true"></span>` : ''}
      <span>${escapeHtml(formatFacetValue(facetConfig, value))}</span>
      ${count == null ? '' : `<span class="solstice__algolia-search-facet-count">${count}</span>`}
    </label>
  `;
}

/** Renders the option-list body shared by 'checkbox' and 'swatch' facets --
 * a search box (Brands only) plus the value rows, with everything past
 * FACET_VISIBLE_LIMIT hidden behind a "View more" toggle. */
/** Just the `<label>` rows, no wrapper -- shared by renderFacetOptionsBody
 * for the initial render; local search toggles these rows in place.
 * @param {FacetConfig} facetConfig
 * @param {Record<string, number>} [facetValues]
 * @param {string[]} [selectedValues]
 */
function renderFacetOptionRows(facetConfig, facetValues, selectedValues) {
  return facetOptionEntries(facetConfig, facetValues)
    .map(({ value, count }, index) => renderFacetOptionRow(facetConfig, value, count,
      (selectedValues || []).some((selected) => facetValueIdentity(facetConfig, selected) === facetValueIdentity(facetConfig, value)), index))
    .join('');
}

/**
 * @param {FacetConfig} facetConfig
 * @param {Record<string, number>} [facetValues]
 * @param {string[]} [selectedValues]
 */
function renderFacetOptionsBody(facetConfig, facetValues, selectedValues) {
  const entries = facetOptionEntries(facetConfig, facetValues);
  if (entries.length === 0) return '<p class="solstice__algolia-search-facet-empty">No options available.</p>';

  const rows = renderFacetOptionRows(facetConfig, facetValues, selectedValues);
  const hasMore = entries.length > FACET_VISIBLE_LIMIT;

  // Markup/spacing/colors pulled directly from Figma's "Section-Brands" node
  // (1922:873) via the Figma MCP tool -- not eyeballed: 14x14 icon box,
  // 8px gap to the input, #101010 1px border, #B1B1B1 placeholder color.
  const search = facetConfig.searchable
    ? `
      <div class="solstice__algolia-search-facet-search">
        <svg class="solstice__algolia-search-facet-search-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <circle cx="6.25" cy="6.25" r="4.5" stroke="#B1B1B1" stroke-width="1.2"></circle>
          <line x1="9.4" y1="9.4" x2="13" y2="13" stroke="#B1B1B1" stroke-width="1.2" stroke-linecap="round"></line>
        </svg>
        <input
          type="search"
          class="solstice__algolia-search-facet-search-input"
          data-facet-search-key="${facetConfig.key}"
          placeholder="Search for ${escapeHtml(facetConfig.label.toLowerCase())}"
          aria-label="Search for ${escapeHtml(facetConfig.label.toLowerCase())}"
        >
      </div>
    `
    : '';

  return `
    ${search}
    <div class="solstice__algolia-search-facet-options" data-facet-options-key="${facetConfig.key}">${rows}</div>
    ${
      hasMore
        ? `<button type="button" class="solstice__algolia-search-facet-view-more" data-facet-view-more-key="${facetConfig.key}">View more</button>`
        : ''
    }
  `;
}

/**
 * Real numeric range slider (two overlaid `<input type="range">` handles --
 * a well-known lightweight technique for a dual-thumb slider without a full
 * custom pointer-drag component), backed by Algolia's own `facets_stats`
 * for this attribute (see buildAlgoliaParams's comment on requesting
 * 'price') so the bounds always match what's actually in this collection,
 * not a guessed/hardcoded range.
 * @param {FacetConfig} facetConfig
 * @param {{min?: number, max?: number}} [stats]
 * @param {{min?: number, max?: number}} [selectedRange]
 */
function renderPriceFacetBody(facetConfig, stats, selectedRange) {
  const min = Math.floor(stats?.min ?? 0);
  const max = Math.ceil(stats?.max ?? 0);
  const currentMin = selectedRange?.min ?? min;
  const currentMax = selectedRange?.max ?? max;

  if (min >= max) return '<p class="solstice__algolia-search-facet-empty">No price range available.</p>';

  return `
    <div class="solstice__algolia-search-price-slider" data-facet-key="${facetConfig.key}" data-min="${min}" data-max="${max}">
      <div class="solstice__algolia-search-price-slider__inputs">
        <input type="number" class="solstice__algolia-search-price-slider__number" data-price-input="min" min="${min}" max="${max}" value="${currentMin}" aria-label="Minimum price">
        <span class="solstice__algolia-search-price-slider__separator" aria-hidden="true">–</span>
        <input type="number" class="solstice__algolia-search-price-slider__number" data-price-input="max" min="${min}" max="${max}" value="${currentMax}" aria-label="Maximum price">
      </div>
      <div class="solstice__algolia-search-price-slider__track">
        <input type="range" class="solstice__algolia-search-price-slider__range" data-price-range="min" min="${min}" max="${max}" value="${currentMin}" aria-label="Minimum price">
        <input type="range" class="solstice__algolia-search-price-slider__range" data-price-range="max" min="${min}" max="${max}" value="${currentMax}" aria-label="Maximum price">
      </div>
      <div class="solstice__algolia-search-price-slider__scale">
        ${Array.from({ length: 6 }, (_, index) => {
          const value = Math.round(min + ((max - min) * index) / 5);
          const label = value >= 1000 ? `${Math.round(value / 1000)}K` : String(value);
          return `<span title="${value}">${label}</span>`;
        }).join('')}
      </div>
    </div>
  `;
}

/**
 * @param {FacetConfig} facetConfig
 * @param {any} facetData
 * @param {any} selectedValue
 * @param {boolean} isOpen
 */
function renderFacetGroup(facetConfig, facetData, selectedValue, isOpen) {
  let body;
  if (facetConfig.type === 'price') {
    body = renderPriceFacetBody(facetConfig, facetData, selectedValue);
  } else {
    body = renderFacetOptionsBody(facetConfig, facetData, selectedValue || []);
  }

  return `
    <details class="solstice__algolia-search-facet-group" data-facet-group-key="${facetConfig.key}" ${isOpen ? 'open' : ''}>
      <summary>${escapeHtml(facetConfig.label)}</summary>
      <div class="solstice__algolia-search-facet-body">${body}</div>
    </details>
  `;
}

/**
 * Shape of `this.refs`, following the same convention as `assets/component.js`'s
 * `Component` base class -- a type-asserted class field, so TS treats each
 * property as whatever this class declares rather than inferring a loose or
 * nullable union. Every one of these elements comes from this section's own
 * unconditional markup or its `static: true` filters block (never a
 * merchant-removable dynamic block), so all are typed as always-present --
 * matching how most of the file already accesses them without `?.`. A few
 * call sites still use `?.` defensively; that stays valid TS on a
 * non-nullable type, just redundant, so nothing had to change there.
 * @typedef {{
 *   facets: Element,
 *   sort: Element[],
 *   grid: Element,
 *   count: Element,
 *   viewTwoColumn: HTMLInputElement,
 *   viewDefault: HTMLInputElement,
 *   loadMoreWrapper: HTMLElement,
 *   loadMoreButton: HTMLElement,
 *   toolbarSkeleton: HTMLElement,
 *   toolbarControls: HTMLElement,
 *   filterTrigger: HTMLElement,
 *   filtersDialog: HTMLDialogElement,
 *   filterClose: HTMLElement,
 *   filterClear: HTMLElement,
 *   filterApply: HTMLElement,
 *   filterDialogCount: HTMLElement,
 *   activeFilters: HTMLElement[],
 * }} SolsticeAlgoliaSearchRefs
 */

/**
 * @typedef {{
 *   page: number,
 *   selectedFacets: SelectedFacets,
 *   renderedCount: number,
 *   nbPages: number,
 *   sortValue: string,
 *   sortIndexName: string | null,
 *   availableSortOptions: typeof SORT_CANDIDATES,
 *   searchTerm: string,
 * }} SolsticeAlgoliaSearchState
 */

/** SORT_CANDIDATES is a fixed non-empty literal array -- this is always its
 * first (Relevance) entry, cast once here instead of at every `[0]` access
 * site, since an indexed array access is otherwise typed as possibly
 * `undefined`. */
const DEFAULT_SORT_OPTION = /** @type {typeof SORT_CANDIDATES[number]} */ (SORT_CANDIDATES[0]);

class SolsticeAlgoliaSearch extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {SolsticeAlgoliaSearchState} */
  #state = {
    page: 0,
    // Applied/actually-queried facet state. Kept in sync with #pendingState
    // (below) except during an open drawer session that hasn't hit Apply yet.
    selectedFacets: {},
    renderedCount: 0,
    nbPages: 0,
    sortValue: DEFAULT_SORT_OPTION.value,
    sortIndexName: null,
    availableSortOptions: [DEFAULT_SORT_OPTION],
    // Empty on collection pages -- only the search page's `?q=` seeds this
    // (see connectedCallback's urlState assignment below).
    searchTerm: '',
  };
  // Drawer-editable facet state -- what checkboxes/swatches/the price slider
  // actually mutate while the panel is open. Only committed into
  // #state.selectedFacets (and only then does anything requery Algolia) when
  // Apply is clicked; Clear resets this back to {} without requerying.
  // Kept equal to #state.selectedFacets at every other time (init, right
  // after Apply commits, right after a chip removal) -- see #onFilterApply/
  // #removeActiveFilter -- so #renderFacets never needs to know which one
  // is "current", it always reads from here.
  /** @type {SelectedFacets} */
  #pendingFacets = {};
  /** @type {Record<string, any>} */
  #lastFacetsResponse = {};
  /** @type {Record<string, any>} */
  #lastFacetsStats = {};
  #renderGeneration = 0;
  /** @type {Promise<any[]>} */
  #bannersPromise = Promise.resolve([]);
  #sortCheckmarkHtml = '';

  /** @type {SolsticeAlgoliaSearchRefs} */
  refs = /** @type {SolsticeAlgoliaSearchRefs} */ ({});

  /**
   * `dataset` properties are typed `string | undefined` (a dataset attribute
   * could theoretically be missing), but connectedCallback's own guard right
   * below (`if (!this.appId || ...) return;`) means every OTHER method only
   * ever runs once these are confirmed non-empty strings -- TS can't carry
   * that guard's narrowing across a mutable instance property, so it's
   * declared here instead of casting at every later call site.
   * @type {string}
   */
  appId;
  /** @type {string} */
  apiKey;
  /** @type {string} */
  indexName;
  /** @type {string | undefined} */
  collectionId;
  /** @type {number} */
  hitsPerPage;
  /** @type {boolean} */
  isOutlet;

  connectedCallback() {
    this.appId = /** @type {string} */ (this.dataset.appId);
    this.apiKey = /** @type {string} */ (this.dataset.searchApiKey);
    this.indexName = /** @type {string} */ (this.dataset.indexName);
    this.collectionId = this.dataset.collectionId;
    this.hitsPerPage = Number(this.dataset.productsPerPage) || 24;
    this.isOutlet = this.dataset.collectionHandle === 'outlet';

    // Cast once at assignment (matching assets/component.js's own
    // `this.refs = /** @type {RefsType<T>} */ (refs);` pattern) rather than
    // expecting every individual querySelector() call to already satisfy
    // the non-null type -- querySelector legitimately returns `Element |
    // null`, and this section's own markup is what actually guarantees
    // these are present, not the query's return type.
    this.refs = /** @type {SolsticeAlgoliaSearchRefs} */ ({
      facets: this.querySelector('[data-ref="facets"]'),
      // Two instances can exist: the desktop toolbar's Sort dropdown and
      // (per Figma's mobile toolbar, which shows no separate Sort control)
      // a duplicate rendered inside the filter drawer for narrow viewports.
      // Both need to render the same live options and stay in sync with
      // whichever one the user actually changes -- see #renderSortOptions.
      sort: [...this.querySelectorAll('[data-ref="sort"], [data-ref="sortMobile"]')],
      grid: this.querySelector('[data-ref="grid"]'),
      count: this.querySelector('[data-ref="count"]'),
      viewTwoColumn: this.querySelector('[data-ref="viewTwoColumn"]'),
      viewDefault: this.querySelector('[data-ref="viewDefault"]'),
      loadMoreWrapper: this.querySelector('[data-ref="loadMoreWrapper"]'),
      loadMoreButton: this.querySelector('[data-ref="loadMoreButton"]'),
      toolbarSkeleton: this.querySelector('[data-ref="toolbarSkeleton"]'),
      toolbarControls: this.querySelector('[data-ref="toolbarControls"]'),
      filterTrigger: this.querySelector('[data-ref="filterTrigger"]'),
      filtersDialog: this.querySelector('[data-ref="filtersDialog"]'),
      filterClose: this.querySelector('[data-ref="filterClose"]'),
      filterClear: this.querySelector('[data-ref="filterClear"]'),
      filterApply: this.querySelector('[data-ref="filterApply"]'),
      filterDialogCount: this.querySelector('[data-ref="filterDialogCount"]'),
      // Two rows: above the grid, and inside the filter drawer (so a
      // customer can remove an applied filter without leaving the drawer) --
      // both stay in sync from the same response, see #renderActiveFilterChips.
      activeFilters: [...this.querySelectorAll('[data-ref="activeFilters"], [data-ref="activeFiltersDrawer"]')],
    });

    if (!this.appId || !this.apiKey || !this.indexName) {
      this.refs.grid.innerHTML = '<li>Algolia is not configured for this section yet.</li>';
      this.#revealToolbar();
      return;
    }

    this.#state.sortIndexName = this.indexName;
    this.#bannersPromise = fetchBanners(this.dataset.collectionHandle);
    pruneExpiredQueryCache();

    // Restore supported URL sorting and filters without discovery requests.
    const urlState = readStateFromUrl();
    this.#state.selectedFacets = urlState.selectedFacets;
    this.#pendingFacets = structuredClone(urlState.selectedFacets);
    this.#state.searchTerm = urlState.searchTerm;
    const collectionDefaultSort = this.dataset.collectionDefaultSort;
    const defaultSortValue = this.collectionId
      ? (['created-desc', 'created-asc'].includes(collectionDefaultSort || '')
        ? collectionDefaultSort
        : 'best-selling')
      : DEFAULT_SORT_OPTION.value;
    const candidate =
      SORT_CANDIDATES.find((c) => c.value === urlState.sortValue) ||
      SORT_CANDIDATES.find((c) => c.value === defaultSortValue) || DEFAULT_SORT_OPTION;
    this.#state.sortValue = candidate.value;
    this.#state.sortIndexName = candidate.indexName || this.indexName;
    this.#state.availableSortOptions = SORT_CANDIDATES;

    this.refs.facets.addEventListener('change', (event) => {
      // event.target is typed as the generic EventTarget by the DOM lib,
      // which has no .matches()/.closest() -- narrowed once here since every
      // listener in this file targets a real DOM Element in practice.
      const target = /** @type {HTMLInputElement} */ (event.target);
      if (target.matches('input[data-facet-key]')) this.#onFacetToggle(target);
      if (target.matches('input[data-price-range]')) this.#onPriceRangeInput(target);
      if (target.matches('input[data-price-input]')) this.#onPriceNumberInput(target);
    });
    this.refs.facets.addEventListener('input', (event) => {
      // 'input' (not 'change') for the range handles -- live-updates the
      // paired number field while dragging, matching how a real price
      // slider feels; the actual pendingFacets commit still only happens on
      // 'change' (drag release) via the listener above.
      const target = /** @type {HTMLInputElement} */ (event.target);
      if (target.matches('input[data-price-range]')) this.#syncPriceInputsFromRange(target);
    });
    this.refs.facets.addEventListener('click', (event) => {
      const target = /** @type {Element} */ (event.target);
      const viewMoreButton = /** @type {HTMLElement | null} */ (target.closest('[data-facet-view-more-key]'));
      if (viewMoreButton) this.#onFacetViewMoreClick(viewMoreButton);
    });
    this.refs.facets.addEventListener('input', (event) => {
      const target = /** @type {HTMLInputElement} */ (event.target);
      if (target.matches('[data-facet-search-key]')) this.#onFacetSearchInput(target);
    });

    this.refs.filterTrigger?.addEventListener('click', () => this.#openFiltersDialog());
    this.refs.filterClose?.addEventListener('click', () => this.refs.filtersDialog?.close());
    this.refs.filtersDialog?.addEventListener('click', (event) => {
      // Native "click outside to close" recipe: a click that lands on the
      // dialog element itself (not a descendant) landed on its backdrop/
      // padding area, since the dialog's own box doesn't fill the viewport
      // (it's a corner-anchored panel, not fullscreen).
      if (event.target === this.refs.filtersDialog) this.refs.filtersDialog.close();
    });
    this.refs.filtersDialog?.addEventListener('close', () => {
      document.body.style.removeProperty('overflow');
      // Discards any unsaved pending edits on close (Close button, Escape,
      // or backdrop click) -- if this close came from Apply instead,
      // #pendingFacets already equals #state.selectedFacets by this point
      // (Apply just committed it), so this is a no-op in that case, and a
      // correct "Cancel" in every other case.
      this.#pendingFacets = structuredClone(this.#state.selectedFacets);
      this.#renderFacets(this.#lastFacetsResponse, this.#lastFacetsStats);
    });
    this.refs.filterClear?.addEventListener('click', () => this.#onFilterClear());
    this.refs.filterApply?.addEventListener('click', () => this.#onFilterApply());

    this.refs.activeFilters.forEach((container) => {
      container.addEventListener('click', (event) => {
        const target = /** @type {Element} */ (event.target);
        const removeButton = target.closest('[data-remove-facet-key]');
        if (removeButton instanceof HTMLElement) {
          this.#removeActiveFilter(removeButton.dataset.removeFacetKey, removeButton.dataset.removeFacetValue);
        }
        if (target.closest('[data-clear-all-filters]')) this.#clearAllActiveFilters();
      });
    });

    this.refs.viewTwoColumn?.addEventListener('change', () => this.refs.grid.setAttribute('product-grid-view', 'two-column'));
    this.refs.viewDefault?.addEventListener('change', () => this.refs.grid.setAttribute('product-grid-view', 'default'));

    // Replaces the native `sorting` snippet's server-rendered (Shopify-sourced)
    // option list with `this.#state.availableSortOptions` (configured locally), and intercepts its change event instead of letting
    // it navigate the page (that's
    // assets/collection-toolbar-sort.js's job, which this section deliberately
    // doesn't use -- see the section's comment on `standalone_sort: true`).
    this.#renderSortOptions();
    this.refs.sort.forEach((container) => container.addEventListener('change', this.#onSortChange));

    this.refs.loadMoreButton?.addEventListener('click', () => this.#loadMore());

    this.#restoreInitialPages(urlState.page);
  }

  /**
   * Restores the cumulative "load more" view a URL's `page` param implies --
   * this grid is append-only (each load-more click adds a page on top of
   * what's already rendered), so reloading straight onto page 3 means
   * fetching and appending pages 0, 1, 2 and 3 in order, not just requesting
   * page 3 alone (which would replace the grid with only that page's slice).
   * Clips to however many pages actually exist once nbPages is known, so a
   * stale/out-of-range `page` in the URL degrades to "load everything there
   * is" instead of erroring.
   * @param {number} targetPage
   */
  async #restoreInitialPages(targetPage) {
    await this.#fetchAndRender();
    while (this.#state.page < targetPage && this.#state.page + 1 < this.#state.nbPages) {
      this.#state.page += 1;
      await this.#fetchAndRender({ append: true });
    }
  }

  /**
   * Rebuilds the sort control's option list (both the mobile <select> and the
   * desktop radio/floating-panel list from snippets/sorting.liquid) from
   * `this.#state.availableSortOptions`, keeping the exact same DOM shape/classes the server-rendered
   * markup used so no CSS/`floating-panel-component`/`accordion-custom-component`
   * behavior changes -- only where the options come from does. Re-run on every
   * sort change too (cheap, short list) so both controls (desktop radios,
   * mobile select) and the status label stay in sync regardless of which one
   * triggered the change.
   */
  #renderSortOptions() {
    const containers = this.refs.sort;
    if (!containers?.length) return;

    const options = this.#state.availableSortOptions;
    const current = options.find((o) => o.value === this.#state.sortValue) || options[0] || DEFAULT_SORT_OPTION;

    containers.forEach((container, containerIndex) => {
      const select = container.querySelector('.sorting-filter__select');
      const optionsInner = container.querySelector('.sorting-filter__options-inner');
      const statusEl = container.querySelector('.facets__status');
      if (!select || !optionsInner) return;

      // The checkmark icon is inlined server-side via `inline_asset_content`;
      // grab its markup once from whatever's already in the DOM (server-
      // rendered on first call) instead of duplicating the SVG in JS -- BUT
      // that only works when snippets/sorting.liquid actually looped over at
      // least one `results.sort_options` entry server-side. On the search
      // page `results: collection` (sections/solstice__algolia-search.liquid)
      // is blank -- there's no `collection` object on the search template --
      // so that loop renders zero options and the checkmark span is never in
      // the initial DOM to capture. The inline SVG fallback below (copied
      // from assets/icon-checkmark.svg, the exact file
      // 'icon-checkmark.svg' | inline_asset_content resolves to) covers that
      // case so the tick still renders even with nothing to scrape.
      if (!this.#sortCheckmarkHtml) {
        this.#sortCheckmarkHtml =
          optionsInner.querySelector('.sorting-filter__checkmark')?.outerHTML ||
          '<span class="svg-wrapper icon-checkmark sorting-filter__checkmark"><svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-checkmark"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
      }

      // Two containers can render the same option list (desktop toolbar +
      // mobile drawer, see `refs.sort`'s comment) -- `containerIndex` keeps
      // their generated ids from colliding, since `<label for>` needs each
      // id to be unique across the whole page.
      const sectionId = `${this.id}-${containerIndex}`;

      select.innerHTML = options.map(
        (option) =>
          `<option value="${escapeHtml(option.value)}" ${option.value === current.value ? 'selected' : ''}>${escapeHtml(
            option.name
          )}</option>`
      ).join('');

      optionsInner.innerHTML = options.map((option) => {
        const inputId = `sort-option-${option.value}-${sectionId}`;
        const isSelected = option.value === current.value;
        return `
          <label for="${inputId}">
            <div class="sorting-filter__option" role="option" aria-selected="${isSelected}" tabindex="${isSelected ? '0' : '-1'}">
              <input
                type="radio"
                name="sort_by-${containerIndex}"
                id="${inputId}"
                value="${escapeHtml(option.value)}"
                class="sorting-filter__input"
                data-option-name="${escapeHtml(option.name)}"
                ${isSelected ? 'checked' : ''}
                aria-hidden="true"
              >
              ${this.#sortCheckmarkHtml}
              <span class="sorting-filter__label">${escapeHtml(option.name)}</span>
            </div>
          </label>
        `;
      }).join('');

      if (statusEl) statusEl.textContent = current.name;
    });
  }

  /** @param {Event} event */
  #onSortChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    // name is now "sort_by-<containerIndex>" (see #renderSortOptions'
    // comment on why -- distinct names per instance so the browser doesn't
    // group both sort UIs' radios into one shared mutually-exclusive set).
    if (!target.name.startsWith('sort_by') || !target.value) return;

    // Closes the desktop/mobile floating-panel <details> the instant an
    // option is picked. snippets/sorting.liquid is rendered here with
    // standalone_sort: true (see sections/solstice__algolia-search.liquid),
    // which opts out of Shopify's own on:change="/updateFilterAndSorting"
    // wiring -- that native handler is what closed the panel before; since
    // this handler replaces it, closing the panel is on us now too. Done
    // unconditionally (even if the clicked option was already selected) so
    // re-clicking the current sort still dismisses the panel.
    const container = event.currentTarget;
    if (container instanceof Element) {
      const detailsEl = container.querySelector('details');
      if (detailsEl) detailsEl.open = false;
    }

    const option = this.#state.availableSortOptions.find((o) => o.value === target.value);
    if (!option || option.value === this.#state.sortValue) return;

    this.#state.sortValue = option.value;
    this.#state.sortIndexName = option.indexName || this.indexName;
    this.#state.page = 0;
    this.#renderSortOptions();
    this.#syncUrl();
    // Reuses the exact same fetch/render path a facet toggle uses -- it
    // already rebuilds params from the current `selectedFacets` state on
    // every call, so a sort change replays whatever filters are active, and
    // (via #onFacetToggle) a filter change replays whatever sort is active.
    this.#fetchAndRender();
  };

  /** Checkbox/swatch facets mutate #pendingFacets only -- batched, per the
   * Apply/Clear design (see #pendingFacets' own comment). Nothing requeries
   * Algolia until #onFilterApply commits this into #state.selectedFacets.
   * @param {HTMLInputElement} input */
  #onFacetToggle(input) {
    const key = input.dataset.facetKey;
    if (!key) return;
    const value = input.value;
    const current = this.#pendingFacets[key] || [];
    const facet = FACET_CONFIG.find((entry) => entry.key === key);
    this.#pendingFacets[key] = updateFacetSelection({ key }, current, value, input.checked,
      facet ? this.#lastFacetsResponse[facet.attribute] : undefined);
  }

  #openFiltersDialog() {
    if (!this.refs.filtersDialog) return;
    document.body.style.overflow = 'hidden';
    this.refs.filtersDialog.showModal();
  }

  /** Resets the drawer's pending selections back to empty -- does NOT
   * requery Algolia or close the dialog (Apply is still required to commit),
   * matching the confirmed Clear-vs-Apply design. Re-renders from the last
   * fetched facet response so the option list/counts stay in place, just
   * unchecked. */
  /** Clears immediately and requeries -- NOT batched like a checkbox toggle
   * is. Clear used to only reset the drawer's pending state (requiring a
   * follow-up Apply to actually take effect), but that meant closing the
   * dialog any other way (X, backdrop, Escape) discarded the Clear too --
   * from a user's perspective indistinguishable from "the button doesn't do
   * anything." Committing right away, the same way #onFilterApply does but
   * with an empty selection, makes it robust to however the dialog gets
   * closed afterward. */
  #onFilterClear() {
    this.#pendingFacets = {};
    this.#state.selectedFacets = {};
    this.#state.page = 0;
    this.#syncUrl();
    this.#fetchAndRender();
    // Closes the same way Apply does (commit, then close) -- previously
    // Clear never closed the dialog at all, which read as "the button
    // doesn't do anything" once you'd already clicked it once.
    this.refs.filtersDialog?.close();
  }

  /** Commits the drawer's pending selections into the applied state, resets
   * to page 0, and requeries -- the one point where a filter change actually
   * reaches Algolia. */
  #onFilterApply() {
    this.#state.selectedFacets = structuredClone(this.#pendingFacets);
    this.#state.page = 0;
    this.#syncUrl();
    this.#fetchAndRender();
    this.refs.filtersDialog?.close();
  }

  /** Chip removal is deliberately NOT batched (unlike the drawer's
   * checkboxes) -- it requeries immediately, matching how the native
   * theme's own filter-remove chips behave. Keeps #pendingFacets in sync so
   * reopening the drawer reflects the removal too.
   * @param {string} [key]
   * @param {string} [value]
   */
  #removeActiveFilter(key, value) {
    if (!key) return;
    if (key === PRICE_FACET_KEY) {
      delete this.#state.selectedFacets[key];
    } else {
      this.#state.selectedFacets[key] = updateFacetSelection({ key }, this.#state.selectedFacets[key] || [], value || '', false);
    }
    this.#pendingFacets = structuredClone(this.#state.selectedFacets);
    this.#state.page = 0;
    this.#syncUrl();
    this.#fetchAndRender();
  }

  #clearAllActiveFilters() {
    this.#state.selectedFacets = {};
    this.#pendingFacets = {};
    this.#state.page = 0;
    this.#syncUrl();
    this.#fetchAndRender();
  }

  /** @param {HTMLInputElement} rangeInput */
  #onPriceRangeInput(rangeInput) {
    this.#syncPriceInputsFromRange(rangeInput);
    this.#commitPendingPriceRange(rangeInput.closest('.solstice__algolia-search-price-slider'));
  }

  /** @param {HTMLInputElement} numberInput */
  #onPriceNumberInput(numberInput) {
    const wrapper = numberInput.closest('.solstice__algolia-search-price-slider');
    if (!wrapper) return;
    const minRange = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('[data-price-range="min"]'));
    const maxRange = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('[data-price-range="max"]'));
    const minNumber = /** @type {HTMLInputElement} */ (wrapper.querySelector('[data-price-input="min"]'));
    const maxNumber = /** @type {HTMLInputElement} */ (wrapper.querySelector('[data-price-input="max"]'));

    let min = Number(minNumber.value);
    let max = Number(maxNumber.value);
    if (min > max) {
      if (numberInput === minNumber) max = min;
      else min = max;
    }

    minNumber.value = String(min);
    maxNumber.value = String(max);
    if (minRange) minRange.value = String(min);
    if (maxRange) maxRange.value = String(max);
    this.#commitPendingPriceRange(wrapper);
  }

  /** Keeps the two overlaid range handles from crossing, and mirrors their
   * live value into the paired number inputs while dragging.
   * @param {HTMLInputElement} rangeInput */
  #syncPriceInputsFromRange(rangeInput) {
    const wrapper = rangeInput.closest('.solstice__algolia-search-price-slider');
    if (!wrapper) return;
    const minRange = /** @type {HTMLInputElement} */ (wrapper.querySelector('[data-price-range="min"]'));
    const maxRange = /** @type {HTMLInputElement} */ (wrapper.querySelector('[data-price-range="max"]'));
    const minNumber = /** @type {HTMLInputElement} */ (wrapper.querySelector('[data-price-input="min"]'));
    const maxNumber = /** @type {HTMLInputElement} */ (wrapper.querySelector('[data-price-input="max"]'));

    let min = Number(minRange.value);
    let max = Number(maxRange.value);
    if (min > max) {
      if (rangeInput === minRange) minRange.value = String(max);
      else maxRange.value = String(min);
      min = Number(minRange.value);
      max = Number(maxRange.value);
    }

    minNumber.value = String(min);
    maxNumber.value = String(max);
  }

  /** @param {Element | null} wrapper */
  #commitPendingPriceRange(wrapper) {
    if (!wrapper) return;
    const min = Number(/** @type {HTMLInputElement} */ (wrapper.querySelector('[data-price-input="min"]')).value);
    const max = Number(/** @type {HTMLInputElement} */ (wrapper.querySelector('[data-price-input="max"]')).value);
    const bounds = {
      min: Number(/** @type {HTMLElement} */ (wrapper).dataset.min),
      max: Number(/** @type {HTMLElement} */ (wrapper).dataset.max),
    };
    // Only store a range if it actually narrows the collection's full
    // bounds -- a slider left at its full extent means "no filter", same as
    // every other facet's empty selection.
    // price_range is the one key whose value isn't a string[] -- see
    // SelectedFacets' own comment on why that's cast here instead of typed
    // in generally.
    this.#pendingFacets[PRICE_FACET_KEY] = /** @type {any} */ (
      min <= bounds.min && max >= bounds.max ? undefined : { min, max }
    );
  }

  /** @param {HTMLElement} button */
  #onFacetViewMoreClick(button) {
    const key = button.dataset.facetViewMoreKey;
    if (!key) return;
    const container = this.refs.facets.querySelector(`[data-facet-options-key="${CSS.escape(key)}"]`);
    if (container) container.dataset.expanded = 'true';
    container?.querySelectorAll('[data-facet-extra]').forEach((row) => row.removeAttribute('hidden'));
    button.remove();
  }

  /** Filter loaded brand rows without changing pending selections.
   * @param {HTMLInputElement} input */
  #onFacetSearchInput(input) {
    const key = input.dataset.facetSearchKey;
    if (!key) return;
    const container = this.refs.facets.querySelector(`[data-facet-options-key="${CSS.escape(key)}"]`);
    if (!container) return;

    const query = input.value.trim().toLocaleLowerCase();
    const expanded = container.dataset.expanded === 'true';
    let matches = 0;
    container.querySelectorAll('.solstice__algolia-search-facet-option').forEach((element) => {
      const row = /** @type {HTMLElement} */ (element);
      const checkbox = /** @type {HTMLInputElement | null} */ (row.querySelector('input[type="checkbox"]'));
      const matchesQuery = (checkbox?.value || '').toLocaleLowerCase().includes(query);
      if (matchesQuery) matches += 1;
      row.hidden = query ? !matchesQuery : !expanded && row.hasAttribute('data-facet-extra');
    });
    const viewMore = /** @type {HTMLElement | null} */ (this.refs.facets.querySelector(`[data-facet-view-more-key="${CSS.escape(key)}"]`));
    if (viewMore) viewMore.hidden = Boolean(query);
    container.querySelector('.solstice__algolia-search-facet-empty')?.remove();
    if (query && !matches) {
      container.insertAdjacentHTML('beforeend', '<p class="solstice__algolia-search-facet-empty" role="status">No matches.</p>');
    }
  }

  /**
   * A page can be entirely stale/deleted-product records (see #renderGrid's
   * comment) and render zero new real cards -- previously "View more" would
   * fetch exactly one page and stop there, so landing on an all-stale page
   * looked like the click did nothing (loading spinner, then no new cards)
   * even though real results existed further on. Keeps advancing through
   * consecutive empty pages automatically instead, same principle as
   * #backfillFirstPage on initial load.
   */
  async #loadMore() {
    if (this.hasAttribute('data-loading')) return;
    this.refs.loadMoreButton?.setAttribute('aria-busy', 'true');
    try {
      const renderedBefore = this.#state.renderedCount;
      while (this.#state.renderedCount === renderedBefore && this.#state.page + 1 < this.#state.nbPages) {
        this.#state.page += 1;
        this.#syncUrl();
        await this.#fetchAndRender({ append: true });
      }
    } finally {
      this.refs.loadMoreButton?.removeAttribute('aria-busy');
    }
  }

  /** Reflects current sort/facet/page state into the URL (Shopify-native
   * param names -- see buildUrlParams) using replaceState, not pushState:
   * a real Shopify filter-form submit adds a history entry per navigation,
   * but that's a full page load each time -- this is AJAX-only, so
   * pushState-per-checkbox-click would flood back/forward with noise. */
  #syncUrl() {
    writeStateToUrl(this.#state);
  }

  async #fetchAndRender({ append = false } = {}) {
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;
    // Invalidate pending Shopify card rendering as soon as a new query starts.
    this.#renderGeneration += 1;
    this.setAttribute('data-loading', '');
    this.refs.grid.setAttribute('aria-busy', 'true');
    if (this.refs.loadMoreButton) this.refs.loadMoreButton.disabled = true;

    try {

    // Reset queries replace old results; pagination keeps existing cards and
    // adds the same placeholders until the appended page finishes loading.
    // Apply starts the request before closing the drawer, so drawer visibility
    // must not suppress feedback for the newly applied filters.
    if (!append) this.#showLoadingState();
    else this.refs.grid.insertAdjacentHTML('beforeend',
      '<li class="product-grid__item product-grid__item--skeleton" data-append-skeleton aria-hidden="true"><div class="solstice__algolia-search-skeleton-card"></div></li>'.repeat(this.hitsPerPage));

    const params = buildAlgoliaParams({
      collectionId: this.collectionId,
      page: this.#state.page,
      hitsPerPage: this.hitsPerPage,
      selectedFacets: this.#state.selectedFacets,
      searchTerm: this.#state.searchTerm,
    });

    let data;
    try {
      data = await fetchAlgoliaResultsCached({
        appId: this.appId,
        apiKey: this.apiKey,
        indexName: this.#state.sortIndexName || this.indexName,
        params,
        signal: abortController.signal,
      });
    } catch (error) {
      if (abortController.signal.aborted || error.name === 'AbortError') return;
      console.error('Algolia search request failed', error);
      if (!append) this.refs.grid.innerHTML = '<li>Something went wrong loading products. Please try again.</li>';
      this.#revealToolbar();
      return;
    }

    // A cache hit (see fetchAlgoliaResultsCached) resolves without ever
    // calling fetch(), so it never throws the AbortError above even if this
    // call has since been superseded by a newer one -- check the signal
    // directly too, or a stale cached response could render over newer
    // results a rapid sort/filter click already triggered.
    if (abortController.signal.aborted) return;

    if (!append) {
      this.#lastFacetsResponse = data.facets || {};
      this.#lastFacetsStats = data.facets_stats || {};
      this.#renderFacets(this.#lastFacetsResponse, this.#lastFacetsStats);
      this.#renderActiveFilterChips();
      this.#state.renderedCount = 0;
      // Removing a pill or clicking "Clear all" INSIDE the drawer replaces
      // the very button that was focused (both re-render via innerHTML) --
      // the browser then drops focus to <body>, breaking the modal
      // <dialog>'s native focus trap (a keyboard user could Tab straight out
      // to the page behind it while the drawer is still visually open, and
      // some browsers visibly flicker the backdrop when that happens).
      // Refocus a control that's never destroyed by a facet/chip re-render.
      if (this.refs.filtersDialog?.open && !this.refs.filtersDialog.contains(document.activeElement)) {
        // preventScroll: the header's close button is always on-screen
        // already (it's outside the scrollable facets body) -- without
        // this, a plain .focus() call scrolls its container into view,
        // which would yank the customer's scroll position back to the top
        // of the drawer on every pill/clear-all removal.
        this.refs.filterClose?.focus({ preventScroll: true });
      }
    }

    this.#state.nbPages = data.nbPages || 0;
    this.#renderCount(data.nbHits || 0);
    // Filters depend on the Algolia response, not slower Shopify card requests.
    this.#revealToolbar();
    await this.#renderGrid(data.hits || [], { append });
    if (abortController.signal.aborted) return;
    this.#updateLoadMoreVisibility();

    if (!append) await this.#backfillFirstPage();
    } finally {
      if (this.#abortController === abortController) {
        this.refs.grid.querySelectorAll('[data-append-skeleton]').forEach((item) => item.remove());
        this.removeAttribute('data-loading');
        this.refs.grid.removeAttribute('aria-busy');
        if (this.refs.loadMoreButton) this.refs.loadMoreButton.disabled = false;
      }
    }
  }

  /**
   * Algolia's per-page hit count can include stale/deleted-product records
   * that #renderGrid silently drops (see its own comment) -- when enough of
   * a page is stale, the customer sees a visibly short first "page" (fewer
   * cards than products_per_page) with "View more" appearing right away,
   * even though there are more real products just a page away. Keeps
   * fetching and appending subsequent pages -- exactly what clicking "View
   * more" would do -- until the visible count reaches hitsPerPage or pages
   * genuinely run out, so "View more" only shows once a full page's worth
   * of real product cards is already on screen (or all results truly are
   * exhausted).
   */
  async #backfillFirstPage() {
    while (this.#state.renderedCount < this.hitsPerPage && this.#state.page + 1 < this.#state.nbPages) {
      this.#state.page += 1;
      this.#syncUrl();
      await this.#fetchAndRender({ append: true });
    }
  }

  /**
   * Swaps the toolbar's loading skeleton (see the section's comment on
   * `data-ref="toolbarSkeleton"`) for the real sort/view/count controls.
   * Called after every response (success or failure), not just the first --
   * paired with `#showLoadingState`, which puts the skeleton back at the
   * start of the next non-append request. Idempotent either way.
   */
  #revealToolbar() {
    this.refs.toolbarControls?.removeAttribute('hidden');
    this.refs.toolbarSkeleton?.setAttribute('hidden', '');
  }

  /**
   * Puts the toolbar and grid back into the same loading-skeleton state
   * shown before the very first response (see the section's comment on
   * `data-ref="toolbarSkeleton"`/`.product-grid__item--skeleton`), so a
   * sort/filter change gives the same visible "it's working" feedback the
   * first page load did, not just silence until new cards pop in.
   */
  #showLoadingState() {
    this.refs.toolbarSkeleton?.removeAttribute('hidden');
    this.refs.toolbarControls?.setAttribute('hidden', '');

    const skeletonCard = '<li class="product-grid__item product-grid__item--skeleton" aria-hidden="true"><div class="solstice__algolia-search-skeleton-card"></div></li>';
    this.refs.grid.innerHTML = skeletonCard.repeat(this.hitsPerPage);
    this.refs.grid.setAttribute('aria-busy', 'true');
  }

  /**
   * @param {Record<string, any>} facets
   * @param {Record<string, any>} facetsStats
   */
  #renderFacets(facets, facetsStats) {
    // This replaces the WHOLE facets panel on every apply/clear/pill-removal/
    // sort-change -- without capturing state first, that silently snapped
    // every group back to its config default (collapsed/expanded) and reset
    // scroll to the top each time, which reads as the whole drawer having
    // "reset" or "closed and reopened" even though the <dialog> itself never
    // actually closes. Preserve whichever groups the customer has manually
    // toggled, and where they'd scrolled to, across the re-render.
    /** @type {Map<string, boolean>} */
    const openStates = new Map();
    this.refs.facets.querySelectorAll('[data-facet-group-key]').forEach((el) => {
      const key = /** @type {HTMLDetailsElement} */ (el).dataset.facetGroupKey;
      if (key) openStates.set(key, /** @type {HTMLDetailsElement} */ (el).open);
    });
    const scrollTop = this.refs.facets.scrollTop;

    this.refs.facets.innerHTML = FACET_CONFIG.filter((f) => !(this.isOutlet && OUTLET_HIDDEN_FACET_KEYS.has(f.key)))
      .map((f) => {
        const data = f.type === 'price' ? facetsStats?.[f.attribute] : facets[f.attribute];
        const selected = this.#pendingFacets[f.key] ?? (f.type === 'price' ? undefined : []);
        const isOpen = openStates.has(f.key) ? /** @type {boolean} */ (openStates.get(f.key)) : !f.collapsedByDefault;
        return renderFacetGroup(f, data, selected, isOpen);
      })
      .join('');

    this.refs.facets.scrollTop = scrollTop;
  }

  /**
   * Active-filter chips (see the section's comment on `data-ref="activeFilters"`):
   * reflects #state.selectedFacets (the APPLIED state, not the drawer's
   * pending edits), one removable pill per selected value plus a "Clear
   * all" when 2+ values are active. Styled to match the native theme's own
   * `.pills__pill` (snippets/filter-remove-buttons.liquid) -- Figma never
   * designed this state (checked the whole file), so visual consistency
   * with the rest of the theme was the deliberate fallback rather than
   * inventing a new look.
   */
  #renderActiveFilterChips() {
    if (!this.refs.activeFilters.length) return;

    /** @type {{key: string, value: string, label: string}[]} */
    const chips = [];
    FACET_CONFIG.forEach((f) => {
      if (f.type === 'price') {
        // Cast: price_range is the one key whose value isn't a string[] --
        // see SelectedFacets' own comment.
        const range = /** @type {{min?: number, max?: number} | undefined} */ (this.#state.selectedFacets[f.key]);
        if (range) {
          const label = `${f.label}: ${range.min ?? ''}–${range.max ?? ''}`;
          chips.push({ key: f.key, value: '', label });
        }
        return;
      }
      const seen = new Set();
      (this.#state.selectedFacets[f.key] || []).forEach((value) => {
        const identity = facetValueIdentity(f, value);
        if (seen.has(identity)) return;
        seen.add(identity);
        chips.push({ key: f.key, value, label: formatFacetValue(f, value) });
      });
    });

    if (chips.length === 0) {
      this.refs.activeFilters.forEach((container) => {
        container.hidden = true;
        container.innerHTML = '';
      });
      return;
    }

    const chipHtml = chips
      .map(
        (chip) => `
          <button type="button" class="solstice__algolia-active-filters__pill" data-remove-facet-key="${chip.key}" data-remove-facet-value="${escapeHtml(chip.value)}">
            <span>${escapeHtml(chip.label)}</span>
            <span class="solstice__algolia-active-filters__pill-remove" aria-hidden="true">×</span>
          </button>
        `
      )
      .join('');

    // Always shown alongside the pills (not just once 2+ are active) --
    // even a single active filter is worth a one-click "start over" option.
    const clearAll = `<button type="button" class="solstice__algolia-active-filters__clear-all" data-clear-all-filters>${escapeHtml('Clear all')}</button>`;

    this.refs.activeFilters.forEach((container) => {
      container.hidden = false;
      container.innerHTML = `${chipHtml}${clearAll}`;
    });
  }

  /**
   * @param {{handle: string, objectID?: string}[]} hits
   * @param {{append: boolean}} options
   */
  async #renderGrid(hits, { append }) {
    const generation = ++this.#renderGeneration;

    if (hits.length === 0 && !append) {
      this.refs.grid.innerHTML = '<li>No products found.</li>';
      return;
    }

    this.refs.grid.setAttribute('aria-busy', 'true');
    const uniqueHits = dedupeHitsByHandle(hits);
    // Only the colour facet pins a card to Algolia's matched variant -- see
    // COLOR_FACET_KEY's own comment. Every other active filter (brand,
    // gender, price, etc.) still narrows WHICH products show up, but each
    // one's card keeps showing its own default featured image/variant.
    const hasColorFilter = (this.#state.selectedFacets[COLOR_FACET_KEY] || []).length > 0;
    // Start requests together, but reveal ordered batches without waiting for
    // the slowest card in the entire page. Banner positions use renderedCount.
    const requests = uniqueHits.map((hit) => fetchCardHtml(hit.handle, hasColorFilter ? hit.objectID : undefined));
    const banners = await this.#bannersPromise;
    let inserted = false;
    let heldCard = null;
    for (let offset = 0; offset < requests.length; offset += 4) {
      const cardHtmls = await Promise.all(requests.slice(offset, offset + 4));

      if (generation !== this.#renderGeneration) return; // superseded by a newer filter/page/load-more change

      // Missing Shopify products return no HTML; ineligible products can return
      // only whitespace because the shared card snippet suppresses its markup.
      // Neither response should be wrapped in an empty grid item.
      /** @type {string[]} */
      const renderedHits = heldCard ? [heldCard] : [];
      heldCard = null;
      cardHtmls.forEach((cardHtml, index) => {
        if (hasRenderableCard(cardHtml)) {
          renderedHits.push(cardHtml.trim());
        } else {
          console.warn(
            `[solstice__algolia-search] Skipped "${uniqueHits[offset + index]?.handle}" -- Shopify returned no renderable card (product missing or all variants hidden).`
          );
        }
      });

      // All of this page's hits can be unrenderable (see the comment above) -- when
      // that leaves nothing renderable on what would otherwise be the first
      // page shown, a blank grid under a real "N items" count reads as
      // broken, not empty. Match the hits.length===0 case above instead of
      // silently rendering nothing.
      const isLastBatch = offset + 4 >= requests.length;
      if (!isLastBatch) heldCard = renderedHits.pop() || null;
      if (renderedHits.length === 0) continue;

      const insertions = computeBannerInsertions(this.#state.renderedCount, renderedHits.length + (heldCard ? 1 : 0), banners.length);

      let html = '';
      renderedHits.forEach((cardHtml, index) => {
        if (!append && !inserted && index === 0) {
          cardHtml = cardHtml.replace('loading="lazy"', 'loading="eager" fetchpriority="high"');
        }
        html += `<li class="product-grid__item">${cardHtml}</li>`;
        for (const insertion of insertions.filter((entry) => entry.afterIndex === index)) {
          const banner = banners[insertion.bannerIndex];
          if (banner) html += buildBannerTileHtml(banner, insertion.variant, insertion.side);
        }
      });

      if (append || inserted) {
        this.refs.grid.insertAdjacentHTML('beforeend', html);
      } else {
        this.refs.grid.innerHTML = html;
      }
      this.#state.renderedCount += renderedHits.length;
      inserted = true;
    }
    if (!inserted && !append) this.refs.grid.innerHTML = '<li>No products found.</li>';
    this.refs.grid.removeAttribute('aria-busy');
  }

  /** @param {number} nbHits */
  #renderCount(nbHits) {
    const text = `${nbHits} ${nbHits === 1 ? 'item' : 'items'}`;
    this.refs.count.textContent = text;
    // Mirrors the same count into the filter drawer's header (Figma's
    // "Mobile Filters Collapsed" node shows one there too) -- both read
    // from the same Algolia response, no separate fetch.
    if (this.refs.filterDialogCount) this.refs.filterDialogCount.textContent = text;
  }

  #updateLoadMoreVisibility() {
    if (!this.refs.loadMoreWrapper) return;
    const hasMore = this.#state.page + 1 < this.#state.nbPages;
    this.refs.loadMoreWrapper.hidden = !hasMore;
  }
}

customElements.define('solstice-algolia-search', SolsticeAlgoliaSearch);
